import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {
  Alert,
  Box,
  Chip,
  Collapse,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  type SxProps,
  type Theme,
} from "@mui/material";
import { isNil } from "lodash";
import { memo, useCallback, useMemo, useState } from "react";
import {
  BenchStepState,
  type BenchEdiTransactionFieldsFragment,
  type BenchOrderReportFieldsFragment,
  type BenchStepFieldsFragment,
} from "../../../generated/graphql";
import {
  formatExpectedEdi,
  MARKER_COLOR,
  orderMarker,
  resolveSelectedOrder,
} from "../bench-report-model";
import { stepLabel } from "../bench-steps";
import BenchOrderTabs from "./bench-order-tabs";
import EdiPayloadPanel from "./edi-payload-panel";

const styles = {
  order: {
    p: 2,
    display: "flex",
    flexDirection: "column",
    gap: 1,
  } satisfies SxProps<Theme>,
  header: {
    display: "flex",
    gap: 1,
    alignItems: "center",
    flexWrap: "wrap",
  } satisfies SxProps<Theme>,
  mono: { fontFamily: "monospace", fontSize: 12 } satisfies SxProps<Theme>,
  dim: { color: "text.secondary" } satisfies SxProps<Theme>,
  payloadCell: { py: 0, borderBottom: 0 } satisfies SxProps<Theme>,
  payload: { py: 1 } satisfies SxProps<Theme>,
};

/** The Outbound EDI table's column count, for the expanded payload row. */
const EDI_COLUMNS = 7;

const STATE_COLOR: Record<
  BenchStepState,
  "success" | "default" | "warning" | "error"
> = {
  [BenchStepState.Done]: "success",
  [BenchStepState.NotApplicable]: "default",
  [BenchStepState.Pending]: "warning",
  [BenchStepState.Blocked]: "error",
  [BenchStepState.Skipped]: "default",
};

/**
 * `plan` is the dry run: each step with the side effects it would cause.
 * `run` is the execution result: the last step's outcome and the step states
 * as they stand after it.
 */
export type BenchReportVariant = "plan" | "run";

type StepRowProps = {
  readonly step: BenchStepFieldsFragment;
  readonly showSideEffects: boolean;
};

const StepRow = memo(({ step, showSideEffects }: StepRowProps) => (
  <TableRow>
    <TableCell>
      <Chip color={STATE_COLOR[step.state]} label={step.state} size="small" />
    </TableCell>
    <TableCell>{stepLabel(step.kind, step.stopRole)}</TableCell>
    <TableCell>{step.reason}</TableCell>
    <TableCell sx={styles.mono}>{formatExpectedEdi(step).join(", ")}</TableCell>
    {showSideEffects && (
      <TableCell>
        {step.sideEffects.map((effect) => (
          <Typography key={effect.kind} variant="body2">
            {effect.kind}
            {isNil(effect.suppressedBy)
              ? ""
              : ` (suppressed in prod by ${effect.suppressedBy})`}
            : {effect.detail}
          </Typography>
        ))}
        {step.refusedInProduction && (
          <Chip color="error" label="refused in prod" size="small" />
        )}
      </TableCell>
    )}
  </TableRow>
));
StepRow.displayName = "StepRow";

type EdiRowProps = { readonly row: BenchEdiTransactionFieldsFragment };

/** One outbound row; expanding it fetches the payload the TMS sent. */
const EdiRow = memo(({ row }: EdiRowProps) => {
  const [open, setOpen] = useState(false);
  const handleToggle = useCallback(() => {
    setOpen((current) => !current);
  }, []);
  return (
    <>
      <TableRow>
        <TableCell padding="checkbox">
          <IconButton
            aria-label={open ? "Hide payload" : "Show payload"}
            size="small"
            onClick={handleToggle}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          {new Date(row.createdAt as string).toLocaleString()}
        </TableCell>
        <TableCell>
          {row.transactionSet}{" "}
          {row.edi214ShipmentStatus ?? row.edi990ActionCode ?? ""}
        </TableCell>
        <TableCell>{row.status}</TableCell>
        <TableCell sx={styles.mono}>{row.receiverIsaId}</TableCell>
        <TableCell sx={styles.mono}>
          {row.tenediMessageId ?? "-"}
          {row.tenediRunIds.length > 0 && (
            <Box component="span" sx={styles.dim}>
              {" "}
              runs {row.tenediRunIds.join(", ")}
            </Box>
          )}
        </TableCell>
        <TableCell>{row.message ?? ""}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={EDI_COLUMNS} sx={styles.payloadCell}>
          <Collapse unmountOnExit in={open}>
            <Box sx={styles.payload}>
              {/* A later round can finish an IN_PROGRESS send under the same
                  uuid; the key remounts the panel so its no-cache query
                  fetches the finished payload. */}
              <EdiPayloadPanel
                key={`${row.status}:${row.message ?? ""}`}
                ediTransactionUuid={row.uuid}
              />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
});
EdiRow.displayName = "EdiRow";

/** One order's EDI rows, as the bench read them back. */
export const EdiTransactionsTable = ({
  rows,
}: {
  readonly rows: BenchEdiTransactionFieldsFragment[];
}) =>
  rows.length === 0 ? (
    <Typography sx={styles.dim} variant="body2">
      No outbound 990/214/210 rows yet.
    </Typography>
  ) : (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell padding="checkbox" />
          <TableCell>Created</TableCell>
          <TableCell>Transaction</TableCell>
          <TableCell>Status</TableCell>
          <TableCell>Receiver</TableCell>
          <TableCell>Tenedi message</TableCell>
          <TableCell>Message</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => (
          <EdiRow key={row.uuid} row={row} />
        ))}
      </TableBody>
    </Table>
  );

type OrderReportProps = {
  readonly order: BenchOrderReportFieldsFragment;
  readonly variant: BenchReportVariant;
};

const OrderReport = memo(({ order, variant }: OrderReportProps) => {
  const marker = orderMarker(order);
  const showSideEffects = variant === "plan";
  return (
    <Paper sx={styles.order} variant="outlined">
      <Box sx={styles.header}>
        <Typography variant="subtitle1">
          {order.orderName ?? order.orderUuid}
        </Typography>
        <Typography sx={styles.dim} variant="body2">
          HAWB {order.hawb ?? "-"} · {order.companyName ?? ""} ·{" "}
          {order.billingContactName ?? ""}
        </Typography>
        {!isNil(order.lastResult) && (
          <Chip
            color={MARKER_COLOR[marker]}
            label={`${order.lastResult.kind ?? ""} ${order.lastResult.outcome}`}
            size="small"
          />
        )}
      </Box>
      {order.refusals.map((refusal) => (
        <Alert key={refusal} severity="error">
          {refusal}
        </Alert>
      ))}
      {!isNil(order.lastResult) && (
        <Typography variant="body2">{order.lastResult.message}</Typography>
      )}
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>State</TableCell>
            <TableCell>Step</TableCell>
            <TableCell>Reason</TableCell>
            <TableCell>Expected EDI</TableCell>
            {showSideEffects && <TableCell>Side effects</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {order.steps.map((step) => (
            <StepRow
              key={`${step.kind}-${step.stopRole ?? ""}-${step.manifestUuid ?? ""}`}
              showSideEffects={showSideEffects}
              step={step}
            />
          ))}
        </TableBody>
      </Table>
      <Typography variant="subtitle2">Outbound EDI</Typography>
      <EdiTransactionsTable rows={order.ediTransactions} />
    </Paper>
  );
});
OrderReport.displayName = "OrderReport";

type BenchReportViewProps = {
  readonly orders: BenchOrderReportFieldsFragment[];
  readonly variant: BenchReportVariant;
  readonly selectedOrderUuid: string | null;
  readonly onSelectOrder: (orderUuid: string) => void;
};

/**
 * A report's orders, one tab each. The parent keeps the selected order's uuid
 * so the tab survives each round's new report, and falls back to the first
 * order when that order leaves the report.
 */
const BenchReportView = ({
  orders,
  variant,
  selectedOrderUuid,
  onSelectOrder,
}: BenchReportViewProps) => {
  const orderUuids = useMemo(
    () => orders.map((order) => order.orderUuid),
    [orders],
  );
  const selected = resolveSelectedOrder(orderUuids, selectedOrderUuid);
  const selectedOrder = useMemo(
    () => orders.find((order) => order.orderUuid === selected),
    [orders, selected],
  );
  if (orders.length === 0) {
    return (
      <Typography sx={styles.dim}>The selection matched no orders.</Typography>
    );
  }
  return (
    <Stack spacing={1}>
      <BenchOrderTabs
        orders={orders}
        selectedOrderUuid={selected}
        onSelect={onSelectOrder}
      />
      {selectedOrder !== undefined && (
        <OrderReport order={selectedOrder} variant={variant} />
      )}
    </Stack>
  );
};

export default BenchReportView;
